import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DialogTitle, DialogContent, DialogActions, Button, Divider, IconButton, Menu, MenuItem, Box } from '@mui/material';
import Dialog from './ActionDialog';
import MenuIcon from '@mui/icons-material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../context/AuthContext';
import { useLogoutConfirmation } from '../hooks/useLogoutConfirmation';
import AccountSettings from '../pages/AccountSettings';
import ConfirmDialog from './ConfirmDialog';
import LanguageToggle from './LanguageToggle';
import ThemeSelector from './ThemeSelector';
import FeedbackDialog from './FeedbackDialog';
import SubscriptionDialog from './SubscriptionDialog';
import { useSubscription } from '../context/SubscriptionContext';
import { FACEBOOK_MESSENGER_URL } from '../constants/commonConstants';

// The account dialog overlays its caller: opening it never unmounts the room.
const AccountMenu = ({ roomActions = [], onLogoutClick, roomId, isLobby = false }) => {
    const { user, logout, refreshUser } = useAuth();
    const { data: subscriptionData } = useSubscription();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState(null);
    const [section, setSection] = useState(null);
    const [accountExpanded, setAccountExpanded] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [subscriptionOpen, setSubscriptionOpen] = useState(false);
    const menuId = useId();
    const { showLogoutConfirmation, setShowLogoutConfirmation, handleLogout, loggingOut } = useLogoutConfirmation(onLogoutClick || (async () => {
        await logout(); navigate('/login', { replace: true });
    }));
    if (!user && !onLogoutClick) return null;
    const close = () => { setAnchor(null); setAccountExpanded(false); };
    const openAccount = (value) => {
        close(); setSection(value);
        // Load current backend eligibility rather than trusting a persisted flag.
        refreshUser();
    };
    const titleKey = section === 'profile' ? 'auth.profile' : section === 'username' ? 'auth.changeUsername'
        : section === 'delete' ? 'auth.deleteAccount'
        : user?.hasPassword ? 'auth.changePassword' : 'auth.setPassword';
    return <>
        <IconButton color="inherit" aria-label={t('auth.accountMenu')} aria-haspopup="menu"
            aria-controls={anchor ? menuId : undefined} aria-expanded={!!anchor} onClick={e => setAnchor(e.currentTarget)}><MenuIcon /></IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={close}
            MenuListProps={{ id: menuId, 'aria-label': t('auth.accountMenu') }}
            PaperProps={{ sx: {
                width: 280, maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100dvh - 72px)', bgcolor: 'background.paper', color: 'text.primary',
                '& .MuiMenuItem-root': { minHeight: 44, py: 1.25, borderBottom: 1, borderColor: 'divider' },
                '& .account-submenu-item': { minHeight: 36, py: 0.75, pl: 4, fontSize: '0.92rem', bgcolor: 'action.hover' },
                '& .MuiDivider-root': { borderColor: 'divider' },
            } }}>
            {user && <MenuItem onClick={() => setAccountExpanded(value => !value)} aria-expanded={accountExpanded}
                sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {t('auth.accountSettings')}{accountExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </MenuItem>}
            {user && accountExpanded && <MenuItem className="account-submenu-item" onClick={() => openAccount('profile')}>{t('auth.profile')}</MenuItem>}
            {user && accountExpanded && <MenuItem className="account-submenu-item" onClick={() => openAccount('username')}>{t('auth.changeUsername')}</MenuItem>}
            {user && accountExpanded && typeof user?.hasPassword === 'boolean' && (user.hasPassword || user.hasGoogleLogin) &&
                <MenuItem className="account-submenu-item" onClick={() => openAccount('password')}>{t(user.hasPassword ? 'auth.changePassword' : 'auth.setPassword')}</MenuItem>}
            {user && accountExpanded && <MenuItem className="account-submenu-item" onClick={() => openAccount('delete')}>{t('auth.deleteAccount')}</MenuItem>}
            {user?.isAdmin && !roomId && <MenuItem onClick={() => { close(); navigate('/admin'); }}>{t('feedback.dashboard')}</MenuItem>}
            {user?.isAdmin && !roomId && <MenuItem onClick={() => { close(); navigate('/admin/subscriptions'); }}>{t('subscription.manage')}</MenuItem>}
            {user?.isAdmin && !roomId && <MenuItem onClick={() => { close(); navigate('/admin/subscription-settings'); }}>{t('subscription.settings')}</MenuItem>}
            {user?.isAdmin && !roomId && <MenuItem onClick={() => { close(); navigate('/admin/app-settings'); }}>{t('subscription.appSettings')}</MenuItem>}
            {!user?.isAdmin && !subscriptionData?.isAdmin && <MenuItem
                {...(subscriptionData?.showSubscribe && subscriptionData?.doExternalSubscribe
                    ? { component: 'a', href: FACEBOOK_MESSENGER_URL, target: '_blank', rel: 'noopener noreferrer', onClick: close }
                    : { onClick: () => { close(); setSubscriptionOpen(true); } })}>
                {t(subscriptionData?.showSubscribe ? 'subscription.title' : 'subscription.status')}
            </MenuItem>}
            {isLobby && <MenuItem onClick={() => { close(); navigate('/'); }}>{t('gameSelection.chooseGame')}</MenuItem>}
            {!roomActions.some(action => action.label === 'menuBar.feedbackSupport') &&
                <MenuItem onClick={() => { close(); setFeedbackOpen(true); }}>{t('feedback.title')}</MenuItem>}
            {roomActions.length > 0 && <Divider />}
            {roomActions.map(action => <MenuItem key={action.label} onClick={() => { close(); action.onClick(); }}>
                {action.icon && <Box component="span" sx={{ display: 'inline-flex', mr: 1 }}>{action.icon}</Box>}{t(action.label)}
            </MenuItem>)}
            <Divider />
            <Box sx={{ px: 2 }}><ThemeSelector /></Box>
            <Box className="menu-account-controls">
                <Button disabled={loggingOut} color="error" startIcon={<PowerSettingsNewIcon />}
                    onClick={() => { close(); setShowLogoutConfirmation(true); }}>{t('auth.logout')}</Button>
                <LanguageToggle />
            </Box>
        </Menu>
        <Dialog open={!!section} onClose={() => setSection(null)} fullWidth maxWidth="sm" aria-labelledby="account-dialog-title">
            <DialogTitle id="account-dialog-title">{t(titleKey)}</DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {section && user && <AccountSettings key={section} embedded section={section} onDeleted={onLogoutClick} />}
            </DialogContent>
            <DialogActions><Button onClick={() => setSection(null)}>{t('common.close')}</Button></DialogActions>
        </Dialog>
        <ConfirmDialog open={showLogoutConfirmation} onConfirm={handleLogout}
            onCancel={() => setShowLogoutConfirmation(false)} onHide={() => setShowLogoutConfirmation(false)}
            title={t('auth.logoutConfirmationTitle')} confirmText={t('auth.logoutConfirmationText')} fullWidth />
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} roomId={roomId} />
        <SubscriptionDialog open={subscriptionOpen} onClose={() => setSubscriptionOpen(false)} />
    </>;
};
export default AccountMenu;
